export const PUBLIC_ROUTE = {
    LOGIN: '/login',
    REGISTER: '/register',
    // Marketing alias — the landing site's CTAs link to app.autocampaign.ai/signup
    // with ?plan= and ?billing= carried from the pricing page.
    SIGNUP: '/signup',
    FORGOT_PASSWORD: '/forgot-password',
    RESET_PASSWORD: '/reset-password',
    /**
     * Landing for the API's OAuth sign-in redirect. The single-use ticket rides
     * in the URL fragment (`#ticket=…`), never the query string.
     */
    OAUTH_CALLBACK: '/auth/callback',
    JOIN: '/join/:slug',
    // Public booking pages. BOOK_MANAGE must be registered before BOOK so the
    // literal "manage" segment is not captured as an org slug.
    BOOK: '/book/:orgSlug/:meetingSlug',
    BOOK_MANAGE: '/book/manage/:token',
    // Literal "r" segment: registered before BOOK so a routing form is not read as a meeting slug.
    BOOK_ROUTING: '/book/:orgSlug/r/:slug',
    ACCEPT_INVITATION: '/accept-invitation',
    /**
     * Fallback for a user who belongs to no organization (removed from their
     * last one, or declined their only invitation). Everything under `/`
     * assumes an active workspace, so it is not a valid destination for them.
     */
    NO_WORKSPACE: '/no-workspace',
    LINK_EXPIRED: '/link-expired',
    // Short-link landing pages. Short URLs use the frontend origin; this route
    // resolves the destination via the public API and redirects the visitor.
    SHORT_LINK_REDIRECT: '/r/:code',
    SHORT_LINK_REDIRECT_TOKEN: '/r/:code/:token',
    /** Hosted public lead form. */
    FORM: '/f/:slug',
} as const;

export const PRIVATE_ROUTES = {
    /**
     * The app's front door. It renders nothing itself — `routes.tsx` redirects
     * it to `TODAY`, which is the landing route. Kept as `'/'` because half the
     * app navigates "home" after finishing something (sign-in, onboarding
     * skip, invitation accept), and those callers should not have to know which
     * page home currently is.
     */
    HOME: '/',
    /**
     * Daily Updates (né Today) — the daily lead-action workspace, and what `/`
     * lands on. Constant names kept as `TODAY*`: renaming them would touch
     * every drill link for zero user value. `/today/*` legacy-redirects here.
     */
    TODAY: '/daily-updates',
    /** Drill list behind every Daily Updates metric tile and "Show all" link. */
    TODAY_LEADS: '/daily-updates/leads',
    /**
     * Custom Dashboards & Widget Framework. Moved off `'/'` when Today became
     * the landing route — `/dashboards` lists the default dashboard, and
     * `DASHBOARD_DETAIL` (unchanged) opens a specific saved one.
     */
    DASHBOARD: '/dashboards',
    /** Custom Dashboards & Widget Framework — a specific saved dashboard. Same `DashboardPage` element as `DASHBOARD`. */
    DASHBOARD_DETAIL: '/dashboards/:id',
    GETTING_STARTED: '/getting-started',
    /** The AI Setup hub: six areas, progress and the next step (same gate as the checklist). */
    AI_SETUP: '/ai-setup',
    /**
     * Outcome reporting: what happened to the leads that came in. First row of
     * the Analytics group, and the drill behind every step of its funnel.
     */
    OUTCOMES: '/outcomes',
    OUTCOMES_LEADS: '/outcomes/leads',
    ANALYTICS: '/analytics',
    ANALYTICS_OVERVIEW: '/analytics/overview',
    ANALYTICS_MARKETING: '/analytics/marketing',
    ANALYTICS_CONVERSATIONS: '/analytics/conversations',
    ANALYTICS_REVENUE: '/analytics/revenue',
    ANALYTICS_APPOINTMENTS: '/analytics/appointments',
    ANALYTICS_DELIVERABILITY: '/analytics/deliverability',
    CAMPAIGNS: '/campaigns',
    CREATE_CAMPAIGN: '/campaigns/new',
    /** Campaign-scoped email builder — reachable only via the wizard's "Edit design" (Task 15). */
    CAMPAIGN_DESIGN: '/campaigns/new/design',
    CAMPAIGN_DETAIL: '/campaigns/:id',
    /** The wizard reopened on a saved DRAFT. Registered above CAMPAIGN_DETAIL. */
    CAMPAIGN_EDIT: '/campaigns/:id/edit',
    CONTACTS: '/contacts',
    IMPORT_CONTACTS: '/contacts/import',
    CONTACT_DETAIL: '/contacts/:contactId',
    SEGMENTS: '/segments',
    SEGMENT_NEW: '/segments/new',
    SEGMENT_DETAIL: '/segments/:segmentId',
    INBOX: '/inbox',
    INBOX_DETAIL: '/inbox/:unifiedConversationId',
    CHANNELS: '/channels',
    CHANNEL_DETAIL: '/channels/:channelType',
    JOIN_LINKS: '/links',
    CREATE_JOIN_LINK: '/links/new',
    JOIN_LINK_DETAIL: '/links/:id',
    EDIT_JOIN_LINK: '/links/:id/edit',
    SHORT_LINK_DETAIL: '/short-links/:id',
    FORMS: '/forms',
    FORM_NEW: '/forms/new',
    /** Optional "start from a template" entry to the create wizard. Its own
     *  route because /forms/new now always starts a blank form — see
     *  template-picker.tsx. */
    FORM_TEMPLATES: '/forms/new/template',
    FORM_DETAIL: '/forms/:id',
    FORM_EDIT: '/forms/:id/edit',
    /** Author-facing preview of a form. Renders the public form exactly as a
     *  visitor sees it, but writes NOTHING — no submission, no contact, no
     *  follow-ups, no analytics (see `lead-form-preview-page.tsx`). */
    FORM_PREVIEW: '/forms/:id/preview',
    JOIN_KEYWORDS: '/keywords',
    CREATE_JOIN_KEYWORD: '/keywords/new',
    JOIN_KEYWORD_DETAIL: '/keywords/:id',
    EDIT_JOIN_KEYWORD: '/keywords/:id/edit',
    POLLS: '/polls',
    POLL_CREATE: '/polls/new',
    POLL_DETAIL: '/polls/:id',
    POLL_ANALYTICS: '/polls/:id/analytics',
    KNOWLEDGE_BASES: '/knowledge-bases',
    KNOWLEDGE_BASE_DETAIL: '/knowledge-bases/:kbId',
    // Legacy AI-agents paths — kept as redirect sources only (routes.tsx maps
    // them to the AI_EMPLOYEE_* routes below, preserving deep links). The old
    // agent-builder feature module itself was removed entirely in Task 18.
    AI_AGENTS: '/ai-agents',
    AI_AGENT_NEW: '/ai-agents/new',
    AI_AGENT_DETAIL: '/ai-agents/:agentId',
    /** How the AI Lead Responder is doing — the measures page (`ai-runs:read`). */
    AI_LEAD_RESPONDER: '/ai-lead-responder',
    AI_EMPLOYEES: '/ai-employees',
    AI_EMPLOYEE_NEW: '/ai-employees/new',
    AI_EMPLOYEE_DETAIL: '/ai-employees/:employeeId',
    CALENDAR: '/calendar',
    MEETING_TYPES: '/meeting-types',
    MEETING_TYPE_NEW: '/meeting-types/new',
    MEETING_TYPE_EDIT: '/meeting-types/:id/edit',
    /** Questions that send a customer to the right appointment type, message or link. */
    ROUTING_FORMS: '/routing-forms',
    ROUTING_FORM_NEW: '/routing-forms/new',
    ROUTING_FORM_EDIT: '/routing-forms/:id/edit',
    /** The outcomes reps can log after an appointment; reached from Appointment types. */
    APPOINTMENT_OUTCOMES: '/appointment-outcomes',
    AVAILABILITY: '/availability',
    BOOKINGS: '/bookings',
    /**
     * One home for everything that runs by itself — playbooks to install and
     * the org's own automations, sequences included. `/workflows` and
     * `/sequences` redirect here; the deep routes below stay put and are the
     * advanced view.
     */
    AUTOMATIONS: '/automations',
    /** Redirect source only — kept so every old link and bookmark still lands. */
    WORKFLOWS: '/workflows',
    WORKFLOW_NEW: '/workflows/new',
    WORKFLOW_BUILDER: '/workflows/:id/builder',
    WORKFLOW_RUNS: '/workflows/:id/runs',
    WORKFLOW_ANALYTICS: '/workflows/:id/analytics',
    BILLING: '/billing',
    MODULE_RECORDS: '/modules/:moduleSlug/records',
    MODULE_RECORD_DETAIL: '/modules/:moduleSlug/records/:recordId',
    TICKETS: '/tickets',
    TICKET_DETAIL: '/tickets/:ticketId',
    OPPORTUNITIES: '/opportunities',
    OPPORTUNITY_REPORTS: '/opportunities/reports',
    OPPORTUNITY_DETAIL: '/opportunities/:opportunityId',
    PIPELINES: '/pipelines',
    CALLS: '/calls',
    ADVERTISING: '/advertising',
    ADVERTISING_GOOGLE: '/advertising/google',
    ADVERTISING_META: '/advertising/meta',
    ADVERTISING_ACCOUNTS: '/advertising/accounts',
    ADVERTISING_CONNECT: '/advertising/connect',
    ADVERTISING_PERFORMANCE: '/advertising/performance',
    ADVERTISING_LEAD_SOURCES: '/advertising/lead-sources',
    /** Redirect source only — see `AUTOMATIONS`. */
    SEQUENCES: '/sequences',
    SEQUENCE_NEW: '/sequences/new',
    SEQUENCE_EDITOR: '/sequences/:id/edit',
    SEQUENCE_DESIGN: '/sequences/:id/design',
    SEQUENCE_ANALYTICS: '/sequences/:id/analytics',
    EMAIL_TEMPLATE_BUILDER: '/email-templates/:id/builder',
    // Top-level homes for Templates and Tags (moved out of Settings in the
    // 2026-07 IA redesign). The SETTINGS_* variants below remain as legacy
    // redirect sources only.
    TEMPLATES: '/templates',
    TEMPLATES_SMS: '/templates/sms',
    TEMPLATES_EMAIL: '/templates/email',
    /** WhatsApp Templates tab (SP2 Task 10). */
    TEMPLATES_WHATSAPP: '/templates/whatsapp',
    /** Full-page WhatsApp template editor — `new` to create, else a template id. */
    TEMPLATES_WHATSAPP_EDITOR: '/templates/whatsapp/:templateId',
    /** Template Marketplace — browse curated system templates (Task 13). */
    TEMPLATES_LIBRARY: '/templates/library',
    TAGS: '/tags',
    /** Agency console — management layer above client workspaces (Agency Phase 1). */
    AGENCY: '/agency',
    AGENCY_CLIENTS: '/agency/clients',
    AGENCY_CLIENT_NEW: '/agency/clients/new',
    AGENCY_SETUPS: '/agency/setups',
    AGENCY_BRANDING: '/agency/branding',
    AGENCY_PRICING: '/agency/pricing',
    SETTINGS: '/settings',
    SETTINGS_ORGANIZATION: '/settings/organization',
    SETTINGS_CHANNELS: '/settings/channels',
    SETTINGS_CHANNEL_DETAIL: '/settings/channels/:channelType',
    SETTINGS_CHANNEL_PROVIDER: '/settings/channels/:channelType/:provider',
    SETTINGS_WHATSAPP: '/settings/whatsapp',
    // Legacy paths — kept as redirect targets only. Deep-links from before
    // the SMS/Email tab merge and the 2026-07 IA redesign (templates and
    // tags moved to top level) still point here.
    SETTINGS_SMS_TEMPLATES: '/settings/sms-templates',
    SETTINGS_TEMPLATES: '/settings/templates',
    SETTINGS_TEMPLATES_SMS: '/settings/templates/sms',
    SETTINGS_TEMPLATES_EMAIL: '/settings/templates/email',
    SETTINGS_EMAIL_DOMAINS: '/settings/email/domains',
    SETTINGS_EMAIL_SENDERS: '/settings/email/senders',
    SETTINGS_MODULE_SETUP: '/settings/module-setup',
    SETTINGS_API_KEYS: '/settings/api-keys',
    SETTINGS_WEBHOOKS: '/settings/webhooks',
    SETTINGS_WEBHOOK_NEW: '/settings/webhooks/new',
    SETTINGS_WEBHOOK_EDIT: '/settings/webhooks/:id/edit',
    SETTINGS_TAGS: '/settings/tags',
    SETTINGS_CALENDARS: '/settings/calendars',
    SETTINGS_FIELD_SERVICE: '/settings/field-service',
    // Legacy AI Phone Answering paths (Phase 10) — kept as redirect sources
    // only: routes.tsx sends them to the employee's Phone tab / the Calls
    // history. Deleted with the old pages in P10-11.
    SETTINGS_AI_PHONE: '/settings/ai-phone',
    SETTINGS_AI_PHONE_CALLS: '/settings/ai-phone/calls',
    SETTINGS_OPT_OUT: '/settings/opt-out',
    SETTINGS_CHAT_WIDGET: '/settings/chat-widget',
    SETTINGS_USERS: '/settings/users',
    // The Users page keeps its tab in `?tab=` (see `users-page.tsx`) — there
    // is no `/settings/users/:tab` route, so these MUST stay query strings.
    // They used to read `/settings/users/members`, which matched no route and
    // dropped the caller onto the top-level 404 (Quick Actions → Invite team,
    // command palette → Team Members).
    SETTINGS_USERS_MEMBERS: '/settings/users?tab=members',
    SETTINGS_USERS_INVITATIONS: '/settings/users?tab=invitations',
    SETTINGS_ROLES: '/settings/roles',
    SETTINGS_VOICE: '/settings/voice',
    /** The Minutes card on Settings → Voice (D13): a hash, like the Users tabs above are queries. */
    SETTINGS_VOICE_MINUTES: '/settings/voice#minutes',
    SETTINGS_VOICE_NUMBER: '/settings/voice/:channelIdentityId',
    PROFILE: '/profile',
} as const;

export const ROUTES = {
    ...PUBLIC_ROUTE,
    ...PRIVATE_ROUTES,
};

/**
 * Query params that hand context between two features' wizards, so a wizard
 * opened from inside another one can return the user to where they were
 * instead of dumping them on its own listing.
 *
 * These are a routing contract, not a feature's private business — they live
 * here because both ends read them and features must not import from one
 * another.
 */
export const NAV_PARAMS = {
    /**
     * Same-origin path the nested wizard should return to. Anyone can type one
     * into the URL bar, so ALWAYS narrow it with `toInternalPath` before
     * handing it to `navigate()`.
     */
    RETURN_TO: 'returnTo',
    /**
     * Id of the record the nested wizard just created, appended to `RETURN_TO`
     * on the way back so the originating screen can preselect it.
     */
    CREATED_ID: 'createdId',
} as const;

/**
 * Pages hosted on the marketing site, outside this bundle.
 *
 * Deliberately kept OUT of `ROUTES`: everything there is a react-router path,
 * and `ROUTES_BY_ROLE` below enumerates those values to build per-role access
 * lists. An absolute URL in that set would be treated as an in-app route.
 *
 * Render these with a plain `<a>`, never a router `<Link>` — `<Link to>` builds
 * a client-side navigation against this origin, so an absolute URL either fails
 * to resolve or is appended to the current path.
 */
export const EXTERNAL_LINKS = {
    TERMS: 'https://www.autocampaign.ai/legal/terms',
    PRIVACY: 'https://www.autocampaign.ai/legal/privacy',
} as const;

export const ROUTES_BY_ROLE: Record<string, string[]> = {
    owner: Object.values(ROUTES),
    admin: Object.values(ROUTES),
    member: [
        ROUTES.HOME,
        ROUTES.TODAY,
        ROUTES.TODAY_LEADS,
        ROUTES.DASHBOARD,
        ROUTES.CAMPAIGNS,
        ROUTES.CONTACTS,
        ROUTES.INBOX,
        ROUTES.CALENDAR,
        ROUTES.MEETING_TYPES,
        ROUTES.MEETING_TYPE_NEW,
        ROUTES.MEETING_TYPE_EDIT,
        ROUTES.ROUTING_FORMS,
        ROUTES.ROUTING_FORM_NEW,
        ROUTES.ROUTING_FORM_EDIT,
        ROUTES.APPOINTMENT_OUTCOMES,
        ROUTES.AVAILABILITY,
        ROUTES.BOOKINGS,
        ROUTES.SETTINGS,
        ROUTES.SETTINGS_ORGANIZATION,
        ROUTES.SETTINGS_CHANNELS,
        ROUTES.SETTINGS_CHANNEL_DETAIL,
        ROUTES.SETTINGS_SMS_TEMPLATES,
        ROUTES.SETTINGS_TEMPLATES,
        ROUTES.SETTINGS_TEMPLATES_SMS,
        ROUTES.SETTINGS_TEMPLATES_EMAIL,
        ROUTES.TEMPLATES,
        ROUTES.TEMPLATES_SMS,
        ROUTES.TEMPLATES_EMAIL,
        ROUTES.TEMPLATES_WHATSAPP,
        ROUTES.TEMPLATES_WHATSAPP_EDITOR,
        ROUTES.TEMPLATES_LIBRARY,
        ROUTES.SETTINGS_EMAIL_DOMAINS,
        ROUTES.SETTINGS_EMAIL_SENDERS,
        ROUTES.PROFILE,
    ],
};
